import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';

const colorMap = {
  'badge-primary':   { bg: '#EEF0FB', color: '#4F63D2', label: 'Azul' },
  'badge-success':   { bg: '#D1FAE5', color: '#065F46', label: 'Verde' },
  'badge-warning':   { bg: '#FEF3C7', color: '#92400E', label: 'Ámbar' },
  'badge-danger':    { bg: '#FEE2E2', color: '#991B1B', label: 'Rojo' },
  'badge-info':      { bg: '#DBEAFE', color: '#1D4ED8', label: 'Cyan' },
  'badge-secondary': { bg: '#F3F4F6', color: '#374151', label: 'Gris' },
};

const inputSt = { width: '100%', padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#111827', backgroundColor: '#fff', outline: 'none', fontFamily: 'inherit' };
const focusSt = e => { e.target.style.borderColor = '#4F63D2'; e.target.style.boxShadow = '0 0 0 3px rgba(79,99,210,0.1)'; };
const blurSt  = e => { e.target.style.borderColor = '#E5E7EB'; e.target.style.boxShadow = 'none'; };

const Label = ({ children, required }) => (
  <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
    {children} {required && <span style={{ color: '#EF4444' }}>*</span>}
  </label>
);

const IconBtn = ({ onClick, title, variant = 'default', children }) => {
  const v = { default: ['#F4F6FB', '#4B5563', '#E5E7EB'], danger: ['#FEE2E2', '#EF4444', '#fecaca'] }[variant];
  return (
    <button onClick={onClick} title={title}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 7, border: 'none', cursor: 'pointer', backgroundColor: v[0], color: v[1], flexShrink: 0 }}
      onMouseEnter={e => e.currentTarget.style.backgroundColor = v[2]}
      onMouseLeave={e => e.currentTarget.style.backgroundColor = v[0]}>
      {children}
    </button>
  );
};

const EditIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ActivityTypes = () => {
  const [types, setTypes]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm]           = useState({ name: '', color: 'badge-primary' });

  const fetchTypes = async () => {
    try { const res = await api.get('/activity-types'); setTypes(res.data); }
    catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchTypes(); }, []);

  const handleNew    = () => { setEditing(null); setForm({ name: '', color: 'badge-primary' }); setShowForm(true); };
  const handleEdit   = t  => { setEditing(t); setForm({ name: t.name, color: t.color }); setShowForm(true); };
  const handleCancel = () => { setShowForm(false); setEditing(null); setForm({ name: '', color: 'badge-primary' }); };

  const handleSubmit = async e => {
    e.preventDefault(); setSubmitting(true);
    try {
      if (editing) await api.put(`/activity-types/${editing.id}`, form);
      else await api.post('/activity-types', form);
      toast.success(editing ? 'Tipo actualizado' : 'Tipo creado');
      handleCancel(); fetchTypes();
    } catch (e) { toast.error('Ocurrió un error al guardar'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('¿Desactivar este tipo de actividad?')) return;
    try { await api.delete(`/activity-types/${id}`); toast.success('Tipo desactivado'); fetchTypes(); }
    catch (e) { toast.error('No se pudo desactivar'); }
  };

  const preview = colorMap[form.color] || colorMap['badge-primary'];

  return (
    <div>
      <PageHeader
        title="Tipos de Actividad"
        subtitle="Categorías para clasificar las actividades en expedientes"
        actions={
          <button onClick={handleNew}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, border: 'none', cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#3d4fb8'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#4F63D2'}>
            <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo Tipo
          </button>
        }
      />

      {/* Formulario inline */}
      {showForm && (
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 20, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="15" height="15" fill="none" stroke="#4F63D2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
            </svg>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
              {editing ? 'Editar Tipo' : 'Nuevo Tipo'}
            </span>
          </div>
          <div style={{ padding: 20 }}>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px auto', gap: '0 16px', alignItems: 'end' }}>
                <div style={{ marginBottom: 14 }}>
                  <Label required>Nombre</Label>
                  <input type="text" name="name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required style={inputSt} onFocus={focusSt} onBlur={blurSt} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <Label>Color</Label>
                  <select value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} style={{ ...inputSt, cursor: 'pointer' }} onFocus={focusSt} onBlur={blurSt}>
                    {Object.entries(colorMap).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <Label>Vista previa</Label>
                  <span style={{ display: 'inline-flex', padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, backgroundColor: preview.bg, color: preview.color }}>
                    {form.name || 'Ejemplo'}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
                <button type="submit" disabled={submitting}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', backgroundColor: '#4F63D2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}>
                  {submitting ? 'Guardando...' : 'Guardar'}
                </button>
                <button type="button" onClick={handleCancel}
                  style={{ padding: '8px 16px', borderRadius: 8, border: '1.5px solid #E5E7EB', backgroundColor: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', color: '#374151' }}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tabla */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 48 }}>
            <div style={{ width: 28, height: 28, border: '3px solid #EEF0FB', borderTopColor: '#4F63D2', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
          </div>
        ) : types.length === 0 ? (
          <EmptyState icon="🏷️" title="No hay tipos de actividad" subtitle="Crea el primer tipo para clasificar actividades" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB' }}>
                {['Nombre', 'Color', ''].map((h, i) => (
                  <th key={i} style={{ padding: '10px 16px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9CA3AF', textAlign: 'left', borderBottom: '1px solid #F3F4F6' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {types.map(type => {
                const cm = colorMap[type.color] || colorMap['badge-secondary'];
                return (
                  <tr key={type.id}
                    style={{ borderBottom: '1px solid #F9FAFB' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#FAFBFF'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ display: 'inline-flex', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, backgroundColor: cm.bg, color: cm.color }}>
                        {type.name}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#6B7280' }}>{cm.label}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
                        <IconBtn onClick={() => handleEdit(type)} title="Editar">
                          <EditIcon />
                        </IconBtn>
                        <IconBtn onClick={() => handleDelete(type.id)} title="Desactivar" variant="danger">
                          <TrashIcon />
                        </IconBtn>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ActivityTypes;
